import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, Button, Container, Paper, 
  Typography, CircularProgress, 
  Stack, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Alert,
  Card, CardContent, Grid, Avatar, alpha, useTheme
} from '@mui/material';
import { Add, Person, School, FamilyRestroom, Group, Security, People, CheckCircle, Visibility, PersonAdd } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchJson } from '../../../utils/fetchJson';
import useAyPhaseReady from '../../../utils/useAyPhaseReady';
import useCurrentUser from '../../../utils/useCurrentUser';
import UserFilter from '../../../components/School_Admin/StaffManagement/UserFilter';
import UserTable from '../../../components/School_Admin/StaffManagement/UserTable';

const UserListPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { ready, activePhase } = useAyPhaseReady();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { user, can } = useCurrentUser();
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    search: ''
  });
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roleCounts, setRoleCounts] = useState({});

  const theme = useTheme();
  const roleNorm = (user?.roleNormalized || user?.role || '').toLowerCase().replace(/\s+/g, '');
  const canDeleteUsers = !!can?.deleteUsers || ['schooladmin','principal','headmaster'].includes(roleNorm);
  const viewerIsSchoolAdmin = roleNorm === 'schooladmin';

  useEffect(() => {
    if (!ready) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, page, pageSize, filters.role, filters.status, filters.search, viewerIsSchoolAdmin]);

  // Auto-refresh on phase/AY changes (dispatched globally)
  useEffect(() => {
    const onPhase = () => { if (ready) fetchData(); };
    const onAy = () => { if (ready) fetchData(); };
    window.addEventListener('app:phase:changed', onPhase);
    window.addEventListener('app:ay:changed', onAy);
    return () => {
      window.removeEventListener('app:phase:changed', onPhase);
      window.removeEventListener('app:ay:changed', onAy);
    };
  }, [ready]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const activeSchoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const headers = {
        ...(activeSchoolId ? { 'X-Scope-School-Id': activeSchoolId } : {}),
        ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {})
      };
      const query = new URLSearchParams({
        page: String(page + 1),
        pageSize: String(pageSize),
        role: filters.role || '',
        status: filters.status || '',
        search: filters.search || ''
      }).toString();

      const listResp = await fetchJson(`/api/users?${query}`, { headers });

      let rolesAvail = [];
      try {
        rolesAvail = await fetchJson('/api/roles/assignable', { headers });
      } catch (_rolesErr) {
        // roles fetch failure is non-fatal; keep empty list
      }

      const rawUsers = listResp.rows || [];
      const staffUsers = rawUsers.filter(u => {
        const roleName = (u.role || '').toLowerCase().replace(/\s+/g, '');
        if (roleName === 'student' || roleName === 'systemadmin' || roleName === 'superadmin') return false;
        if (!viewerIsSchoolAdmin && roleName === 'schooladmin') return false;
        return true;
      });

      setUsers(staffUsers);
      setTotal(staffUsers.length);

      // /api/roles/assignable already returns resolved roles (system templates + school custom roles)
      // with override semantics. Keep only relevant roles for the current phase.
      let visibleRoles = Array.isArray(rolesAvail)
        ? rolesAvail
        : (rolesAvail?.roles || rolesAvail?.data || []);

      visibleRoles = visibleRoles.filter((r) => {
        if (r.schoolId == null) return !!r.isTemplate || !!r.isSystemRole;
        if (!activeSchoolId) return true;
        return String(r.schoolId) === String(activeSchoolId);
      });
      const phase = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toLowerCase();
      if (phase) {
        visibleRoles = visibleRoles.filter(r => {
          const metaPhase = r.metadata && r.metadata.phaseCode
            ? String(r.metadata.phaseCode).toLowerCase()
            : null;
          if (metaPhase && metaPhase !== phase) return false;
          const nmSlug = (r.name || '').toString().toLowerCase().replace(/\s+/g,'');
          if (phase === 'sec' && nmSlug === 'headmaster') return false;
          if (phase === 'nur_pri' && nmSlug === 'principal') return false;
          return true;
        });
      }

      // Staff management: never show Student, SystemAdmin, or system-only template roles in dropdowns
      visibleRoles = visibleRoles.filter((r) => {
        const nmSlug = (r?.name || '').toString().toLowerCase().replace(/\s+/g, '');
        if (nmSlug === 'student' || nmSlug === 'systemadmin') return false;
        // Exclude system-scoped template roles (e.g. ITManager, SalesRep) - not relevant to schools
        if (r.schoolId == null && r.isSystemRole && !r.isTemplate) return false;
        const scope = (r.metadata?.templateScope || r.templateScope || '').toString().toLowerCase();
        if (r.schoolId == null && scope === 'system') return false;
        return true;
      });

      // Enforce hierarchy: only SchoolAdmin can see/assign SchoolAdmin
      if (!viewerIsSchoolAdmin) {
        visibleRoles = visibleRoles.filter((r) => {
          const nmSlug = (r?.name || '').toString().toLowerCase().replace(/\s+/g, '');
          return nmSlug !== 'schooladmin';
        });
      }
      setRoles(visibleRoles);
      
      // Calculate role counts
      calculateRoleCounts(staffUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.data
      });
      
      if (error.response?.status === 403) {
        enqueueSnackbar('Access denied. You may not have permission to view this data.', { variant: 'error' });
      } else if (error.response?.status === 401) {
        enqueueSnackbar('Authentication required. Please log in again.', { variant: 'error' });
      } else {
        enqueueSnackbar(t('users.errorFetch'), { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateRoleCounts = (userList) => {
    const counts = {};
    
    userList.forEach(user => {
      // Backend returns role as a string, not roleId
      const roleName = user.role ? user.role.toLowerCase() : '';
      if (roleName) {
        counts[roleName] = (counts[roleName] || 0) + 1;
      }
    });
    
    setRoleCounts(counts);
  };

  const handleCreate = () => {
    // Route defined in App.js
    navigate('/user-form');
  };

  const handleEdit = (userId) => {
    // Use the canonical user form route and pass userId via navigation state
    navigate('/user-form', { state: { userId } });
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('users.confirmDelete'))) return;
    
    try {
      await fetchJson(`/api/users/${userId}`, {
        method: 'DELETE'
      });
      
      enqueueSnackbar(t('users.deleted'), { variant: 'success' });
      fetchData();
    } catch (error) {
      enqueueSnackbar(t('users.errorDelete'), { variant: 'error' });
      console.error('Failed to delete user:', error);
    }
  };

  const handleStatusChange = async (userId, status) => {
    try {
      await fetchJson(`/api/users/${userId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      
      enqueueSnackbar(t('users.statusUpdated'), { variant: 'success' });
      fetchData();
    } catch (error) {
      enqueueSnackbar(t('users.errorStatusUpdate'), { variant: 'error' });
      console.error('Failed to update user status:', error);
    }
  };

  const openChangeRole = (user) => {
    setSelectedUser(user);
    setSelectedRoleId('');
    setRoleDialogOpen(true);
  };

  const closeChangeRole = () => {
    setRoleDialogOpen(false);
    setSelectedUser(null);
    setSelectedRoleId('');
  };

  const submitChangeRole = async () => {
    if (!selectedUser || !selectedRoleId) return;
    try {
      // There is no /api/user-roles router anywhere on the server, so this
      // silently 404'd and the role never changed. updateUser takes roleId.
      await fetchJson(`/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roleId: selectedRoleId })
      });
      enqueueSnackbar(t('users.roleUpdated'), { variant: 'success' });
      closeChangeRole();
      fetchData();
    } catch (error) {
      enqueueSnackbar(t('users.errorRoleUpdate'), { variant: 'error' });
      console.error('Failed to update role:', error);
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      await fetchJson(`/api/users/${userId}/resend-invite`, { method: 'POST' });
      enqueueSnackbar(t('users.inviteSent'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('users.errorInvite'), { variant: 'error' });
      console.error('Failed to resend invite:', e);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      await fetchJson(`/api/users/${userId}/reset-password`, { method: 'POST' });
      enqueueSnackbar(t('users.passwordReset'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('users.errorPasswordReset'), { variant: 'error' });
      console.error('Failed to reset password:', e);
    }
  };

  const handleFilterChange = useCallback((name, value) => {
    if (name === 'refresh') {
      // Handle refresh
      fetchData();
      return;
    }
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(0); // Reset to first page when filters change
  }, [fetchData]);

  const getRoleName = (roleId) => {
    const role = roles.find(r => String(r.id) === String(roleId));
    return role ? role.name : '';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const stats = useMemo(() => {
    const all = users;
    return {
      total: all.length,
      active: all.filter(u => u.status === 'active').length,
      teachers: all.filter(u => {
        const r = (u.role || '').toLowerCase();
        return r.includes('teacher') || r.includes('classmaster') || r.includes('subjecthead');
      }).length,
      recentlyActive: all.filter(u => {
        if (!u.lastActive) return false;
        return (Date.now() - new Date(u.lastActive).getTime()) < 24 * 60 * 60 * 1000;
      }).length
    };
  }, [users]);

  const summaryCards = [
    { label: 'Total Users', value: stats.total, icon: <People />, color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.08) },
    { label: 'Active', value: stats.active, icon: <CheckCircle />, color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.08) },
    { label: 'Teachers', value: stats.teachers, icon: <School />, color: '#2563eb', bg: alpha('#2563eb', 0.08) },
    { label: 'Active Today', value: stats.recentlyActive, icon: <Visibility />, color: theme.palette.info.main, bg: alpha(theme.palette.info.main, 0.08) },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('users.title')}</Typography>
          <Typography variant="body2" color="text.secondary">Manage school user accounts, roles, and permissions</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<PersonAdd />} variant="contained" onClick={handleCreate} disabled={loading}>
            {t('users.addUser')}
          </Button>
        </Stack>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((c) => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Card sx={{ bgcolor: c.bg, border: 'none', boxShadow: 'none' }}>
              <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>{c.label}</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: c.color }}>{c.value}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(c.color, 0.15), color: c.color, width: 44, height: 44 }}>
                    {c.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <UserFilter 
            filters={filters} 
            roles={roles}
            onFilterChange={handleFilterChange}
            loading={loading}
          />
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {users.length > 0 ? (
            <UserTable 
              users={users}
              roles={roles}
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(0); }}
              canAssignRoles={!!can?.assignRoles}
              canManageUsers={!!can?.manageUsers}
              canInviteUsers={!!can?.inviteUsers}
              canResetPasswords={!!can?.resetPasswords}
              canDeleteUsers={canDeleteUsers}
              getRoleName={getRoleName}
              getStatusColor={getStatusColor}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onStatusChange={handleStatusChange}
              onChangeRole={openChangeRole}
              onResendInvite={handleResendInvite}
              onResetPassword={handleResetPassword}
            />
          ) : (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {t('users.noUsersFound')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('users.tryDifferentFilters')}
              </Typography>
            </Card>
          )}
        </>
      )}

      <Dialog open={roleDialogOpen} onClose={closeChangeRole} maxWidth="xs" fullWidth>
        <DialogTitle>{t('users.changeRole')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('users.form.role')}</InputLabel>
            <Select
              label={t('users.form.role')}
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
            >
              {roles.map(r => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeChangeRole}>{t('common.cancel')}</Button>
          <Button onClick={submitChangeRole} variant="contained">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserListPage;